import { useState } from 'react';
import { useForm } from '@mantine/form';
import { TextInput, Textarea, Button, Stack, Alert } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { track } from '../../utils/analytics';
import type { ContactRequest } from '../../types/apiResponses';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactFormValues {
  name: string;
  company: string;
  email: string;
  message: string;
  website: string; // honeypot — real users never see or fill this
}

export default function ContactForm() {
  const { t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<ContactFormValues>({
    initialValues: { name: '', company: '', email: '', message: '', website: '' },
    validate: {
      name: (value) => (value.trim() ? null : t('contact.errorRequired')),
      email: (value) => (EMAIL_REGEX.test(value) ? null : t('contact.errorEmail')),
      message: (value) => (value.trim() ? null : t('contact.errorRequired')),
    },
  });

  const mutation = useMutation({
    mutationFn: (data: ContactRequest) => apiClient.contact.send(data),
    onSuccess: () => {
      setSubmitted(true);
      track('contact_submit');
      form.reset();
    },
  });

  const handleSubmit = form.onSubmit((values) => {
    if (values.website) {
      // Honeypot filled in — a bot, most likely. Pretend success, skip the API call.
      setSubmitted(true);
      return;
    }
    mutation.mutate({
      name: values.name,
      company: values.company || undefined,
      email: values.email,
      message: values.message,
    });
  });

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('contact.heading')}</h2>
          <p className="text-lg text-gray-600">{t('contact.subheading')}</p>
        </div>

        {submitted ? (
          <Alert color="green" data-testid="contact-success">
            {t('contact.success')}
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Stack>
              <TextInput label={t('contact.nameLabel')} required {...form.getInputProps('name')} />
              <TextInput label={t('contact.companyLabel')} {...form.getInputProps('company')} />
              <TextInput
                label={t('contact.emailLabel')}
                type="email"
                required
                {...form.getInputProps('email')}
              />
              <Textarea
                label={t('contact.messageLabel')}
                required
                minRows={4}
                {...form.getInputProps('message')}
              />

              {/* Honeypot: visually hidden, hidden from assistive tech, and non-focusable. */}
              <div className="sr-only" aria-hidden="true">
                <TextInput
                  label={t('contact.honeypotLabel')}
                  tabIndex={-1}
                  autoComplete="off"
                  {...form.getInputProps('website')}
                />
              </div>

              {mutation.isError && (
                <Alert color="red" data-testid="contact-error">
                  {t('contact.error')}
                </Alert>
              )}

              <Button type="submit" loading={mutation.isPending}>
                {t('contact.submit')}
              </Button>
            </Stack>
          </form>
        )}
      </div>
    </section>
  );
}
